import React, { useEffect, useState } from "react";
import { Form, Modal, Confirm } from "semantic-ui-react";
import { useFormik } from "formik";
import { Grade, Subject, CourseTeacher, User } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { initialValues, validationSchema } from "./AssignTeacherForm.form";
import { withEmptyOption } from "../../../../utils";
import "./AssignTeacherForm.scss";
import { CustomTable } from "../../../Shared";

const courseTeacherController = new CourseTeacher();
const subjectController = new Subject();
const gradeController = new Grade();
const userController = new User();

export function AssignTeacherForm(props) {
  const { onClose, academicCycle } = props;
  const { accessToken } = useAuth();
  const [courseTeachers, setCourseTeachers] = useState(null);
  const [gradesOptions, setGradesOptions] = useState([]);
  const [subjectsOptions, setSubjectsOptions] = useState([]);
  const [usersOptions, setUsersOptions] = useState([]);
  const [loading, setLoading] = useState(true)

  const [reload, setReload] = useState(false);
  const onReload = () => setReload((prevState) => !prevState);

  const [teacherActive, setTeacherActive] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);

  const onOpenCloseConfirm = (row) => {
    if (!row) {
      setShowConfirm((prevState) => !prevState);
      return;
    }
    setTeacherActive(row);
    setConfirmMessage(
      `¿Eliminar la relación del docente "${row.User?.firstname} ${
        row.User?.lastname ?? ""
      } ${row.User?.secondLastname ?? ""}: ${row.Grade?.name} - ${
        row.Subject?.name
      }"?`
    );
    setShowConfirm((prevState) => !prevState);
  };

  const formik = useFormik({
    initialValues: initialValues(),
    validationSchema: validationSchema(),
    validateOnChange: false,
    onSubmit: async (formValue) => {
      try {
        const data = {
          userId: formValue.userId,
          gradeId: formValue.gradeId,
          subjectId: formValue.subjectId,
          academicCycleId: academicCycle.id,
        };
        await courseTeacherController.createCourseTeacher(accessToken, data);
        formik.values.subjectId = null;
        formik.values.userId = null;
        onReload();
      } catch (error) {
        console.error(error);
      }
    },
  });

  useEffect(() => {
    (async () => {
      try {
        setGradesOptions([]);
        //const response = await gradeController.getGrades(accessToken);
        const response = await gradeController.getGradesByLevelId(
          academicCycle.levelId,
          accessToken
        );
        const options = response.map((grade) => ({
          value: grade.id,
          text: grade.name,
        }));
        setGradesOptions(options);
      } catch (error) {
        console.error(error);
      }
    })();
  }, [academicCycle, accessToken]);

  useEffect(() => {
    (async () => {
      try {
        setUsersOptions([]);
        const response = await userController.getTeachers();
        const options = response.map((user) => ({
          value: user.id,
          text: `${user.firstname} ${user.lastname ?? ""} ${
            user.secondLastname ?? ""
          }`,
        }));
        setUsersOptions(options);
      } catch (error) {
        console.error(error);
      }
    })();
  }, [academicCycle, accessToken]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        if (formik.values.gradeId) {
          setSubjectsOptions([]);
          setCourseTeachers(false);
          const response = formik.values.gradeId
            ? await courseTeacherController.getCourseTeachersByAcademicCycle(
                accessToken,
                academicCycle.id,
                formik.values.gradeId
              )
            : null;
          const availableSubjectsOptions = response.availableSubjects.map(
            (subject) => ({
              value: subject.id,
              text: subject.name,
            })
          );
          setCourseTeachers(response.assignedSubjects);
          setSubjectsOptions(availableSubjectsOptions);
        }else{
          setSubjectsOptions([]);
          setCourseTeachers([]);
        }
      } catch (error) {
        console.error(error);
      }
      setLoading(false);
    })();
  }, [reload, academicCycle, formik.values.gradeId, accessToken]);

  const onDelete = async () => {
    try {
      await courseTeacherController.deleteCourseTeacher(
        accessToken,
        teacherActive.id
      );
      onReload();
      onOpenCloseConfirm();
    } catch (error) {
      console.error(error);
    }
  };

  const tableConfig = {
    headers: ["Grado", "Asignatura", "Docente", "Acciones"],
    fields: [
      {
        field: "Grade.name",
        dataType: "text",
      },
      {
        field: "Subject.name",
        dataType: "text",
      },
      {
        field: "User.lastname+ +User.secondLastname+ +User.firstname",
        dataType: "text",
      },
      {
        buttons: [
          {
            color: "red",
            icon: "trash",
            placeholder: "Eliminar relación",
            onClick: onOpenCloseConfirm,
          },
        ],
      },
    ],
    columnWidths: ["20%", "30%", "40%", "10%"],
  };

  return (
    <>
      <Form className="assign-teacher__form" onSubmit={formik.handleSubmit}>
        <div className="assign-teacher-row">
          <Form.Select
            className="edunova-select"
            name="gradeId"
            label="Grado académico"
            placeholder="Selecciona un grado académico"
            clearable
            options={withEmptyOption(gradesOptions, "Selecciona un grado académico")}
            onChange={(e, { value }) => {
              formik.setFieldValue("gradeId", value);
              formik.setFieldValue("subjectId", null);
              formik.setFieldValue("userId", null);
            }}
            value={formik.values.gradeId}
            error={formik.errors.gradeId}
          />
          <Form.Select
            className="edunova-select"
            name="subjectId"
            label="Asignatura"
            placeholder="Selecciona una asignatura"
            clearable
            options={withEmptyOption(subjectsOptions, "Selecciona una asignatura")}
            onChange={(e, { value }) =>
              formik.setFieldValue("subjectId", value)
            }
            value={formik.values.subjectId}
            error={formik.errors.subjectId}
          />
        </div>
        <div className="assign-teacher-row">
          <Form.Select
            className="edunova-select"
            name="userId"
            label="Docente"
            placeholder="Selecciona un docente"
            clearable
            options={withEmptyOption(usersOptions, "Selecciona un docente")}
            onChange={(e, { value }) => formik.setFieldValue("userId", value)}
            value={formik.values.userId}
            error={formik.errors.userId}
          />
          <div className="button-field">
            <div className="label-spacer">&nbsp;</div>
            <Form.Button
              type="submit"
              primary
              fluid
              loading={formik.isSubmitting}
            >
              Asignar docente
            </Form.Button>
          </div>
        </div>
      </Form>
      <Modal.Content className="assign-teacher__list" scrolling>
        <CustomTable
          color="teal"
          tableConfig={tableConfig}
          data={courseTeachers}
          onReload={onReload}
          loading={loading}
        />
      </Modal.Content>
      <Confirm
        confirmButton="Aceptar"
        cancelButton="Cancelar"
        open={showConfirm}
        onCancel={onOpenCloseConfirm}
        onConfirm={onDelete}
        content={confirmMessage}
        size="mini"
      />
    </>
  );
}
